import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiClientError } from '../api/apiClient';
import {
  fetchMyTransportCompany,
  fetchMyPilotProfile,
} from '../api/services';
import { useAuthentication } from '../store/AppContext';
import type { Pilot, TransportCompany } from '../types';

// ============================================================
// GENERIC ASYNC FETCH HOOK
// ============================================

interface FetchState<TData> {
  data: TData | null;
  isLoading: boolean;
  error: string | null;
}

interface UseFetchOptions {
  enabled?: boolean;
}

export function useFetch<TData>(
  fetchFunction: () => Promise<TData>,
  dependencies: unknown[] = [],
  options: UseFetchOptions = {}
): FetchState<TData> & { refetch: () => void } {
  const { enabled = true } = options;
  const [fetchState, setFetchState] = useState<FetchState<TData>>({
    data: null,
    isLoading: enabled,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchFunctionRef = useRef(fetchFunction);
  fetchFunctionRef.current = fetchFunction;

  const executeFetch = useCallback(async () => {
    if (!enabled) return;

    // Cancel any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setFetchState({ data: null, isLoading: true, error: null });

    try {
      const result = await fetchFunctionRef.current();
      setFetchState({ data: result, isLoading: false, error: null });
    } catch (fetchError) {
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        return; // Request was intentionally aborted
      }

      const errorMessage =
        fetchError instanceof ApiClientError
          ? fetchError.apiError.message
          : 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';

      setFetchState({ data: null, isLoading: false, error: errorMessage });
    }
  }, [enabled, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    executeFetch();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [executeFetch]);

  return { ...fetchState, refetch: executeFetch };
}

// ============================================================
// PAGINATED FETCH HOOK
// ============================================================

interface PaginatedState<TData> {
  items: TData[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

interface PaginatedFetchResult<TData> {
  items: TData[];
  totalItems: number;
  totalPages: number;
}

export function usePaginatedFetch<TData, TFilters>(
  fetchFunction: (filters: TFilters & { page: number }) => Promise<PaginatedFetchResult<TData>>,
  initialFilters: TFilters,
  initialPage = 1
): PaginatedState<TData> & {
  setPage: (page: number) => void;
  setFilters: (filters: Partial<TFilters>) => void;
  currentFilters: TFilters & { page: number };
  refetch: () => void;
} {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentFilters, setCurrentFilters] = useState<TFilters>(initialFilters);
  const [paginatedState, setPaginatedState] = useState<PaginatedState<TData>>({
    items: [],
    totalItems: 0,
    currentPage: initialPage,
    totalPages: 1,
    isLoading: true,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const executeFetch = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setPaginatedState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await fetchFunction({ ...currentFilters, page: currentPage });
      setPaginatedState({
        items: result.items,
        totalItems: result.totalItems,
        totalPages: result.totalPages,
        currentPage,
        isLoading: false,
        error: null,
      });
    } catch (fetchError) {
      if (fetchError instanceof Error && fetchError.name === 'AbortError') return;

      const errorMessage =
        fetchError instanceof ApiClientError
          ? fetchError.apiError.message
          : 'Nie udało się załadować danych.';

      setPaginatedState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [currentPage, currentFilters, fetchFunction]);

  useEffect(() => {
    executeFetch();
    return () => { abortControllerRef.current?.abort(); };
  }, [executeFetch]);

  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const setFilters = useCallback((newFilters: Partial<TFilters>) => {
    setCurrentFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  return {
    ...paginatedState,
    setPage,
    setFilters,
    currentFilters: { ...currentFilters, page: currentPage },
    refetch: executeFetch,
  };
}

// ============================================================
// DEBOUNCE HOOK
// ============================================================

export function useDebounce<TValue>(value: TValue, delayMs: number): TValue {
  const [debouncedValue, setDebouncedValue] = useState<TValue>(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delayMs]);

  return debouncedValue;
}

// ============================================================
// LOCAL STORAGE HOOK
// ============================================================

export function useLocalStorage<TValue>(
  storageKey: string,
  defaultValue: TValue
): [TValue, (value: TValue) => void] {
  const [storedValue, setStoredValue] = useState<TValue>(() => {
    try {
      const storedItem = window.localStorage.getItem(storageKey);
      return storedItem ? (JSON.parse(storedItem) as TValue) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (value: TValue) => {
      try {
        setStoredValue(value);
        window.localStorage.setItem(storageKey, JSON.stringify(value));
      } catch {
        console.error(`[useLocalStorage] Failed to save key: ${storageKey}`);
      }
    },
    [storageKey]
  );

  return [storedValue, setValue];
}

// ============================================================
// USER PROFILE HOOK
// ============================================================

export interface CompanyUserProfileData {
  id: number;
  documentId: string;
  type: 'COMPANY';
  fullData: TransportCompany;
}

export interface PilotUserProfileData {
  id: number;
  documentId: string;
  type: 'PILOT';
  fullData: Pilot;
}

export type UserProfileData = CompanyUserProfileData | PilotUserProfileData;

export function useUserProfile() {
  const { currentUser } = useAuthentication();

  return useFetch<UserProfileData | null>(
    async (): Promise<UserProfileData | null> => {
      if (!currentUser) return null;

      // New logic: Use membership and fetch deep data
      if (currentUser.membership) {
        const { USER_TYPE, company_transport, pilot } = currentUser.membership;

        if (USER_TYPE === 'TRANSPORT_COMPANY' && company_transport) {
          const id = typeof company_transport === 'object' ? company_transport.id : company_transport;
          const documentId = typeof company_transport === 'object' ? company_transport.documentId : '';
          const fullData = await fetchMyTransportCompany(currentUser.id);
          return { id, documentId, type: 'COMPANY', fullData: fullData.data };
        }

        if (USER_TYPE === 'PILOT_B2B_COMPANY' && pilot) {
          const id = typeof pilot === 'object' ? pilot.id : pilot;
          const documentId = typeof pilot === 'object' ? pilot.documentId : '';
          const fullData = await fetchMyPilotProfile(currentUser.id);
          return { id, documentId, type: 'PILOT', fullData: fullData.data };
        }
      }

      return null;
    },
    [currentUser?.id, currentUser?.membership],
    { enabled: !!currentUser }
  );
}
